import apiClient from './config/axios';

/**
 * Authentication API module
 * Handles all authentication-related API calls
 */
export const authApi = {
    /**
     * Check authentication status - verify current token
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    checkAuth: async () => {
        try {
            const response = await apiClient.get('/api/website-user/me');
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to verify authentication',
            };
        }
    },

    /**
     * Login user with email and password
     * @param {string} email - User email
     * @param {string} password - User password
     * @returns {Promise<{success: boolean, data?: {user: object, token: string}, message?: string}>}
     */
    login: async (email, password) => {
        try {
            const response = await apiClient.post('/api/login/website-user', {
                email,
                password,
            });
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Login failed',
            };
        }
    },

    /**
     * Register new user
     * @param {object} userData - User registration data
     * @param {string} userData.firstName - User first name
     * @param {string} userData.lastName - User last name
     * @param {string} userData.email - User email
     * @param {string} userData.password - User password
     * @returns {Promise<{success: boolean, data?: {user: object, token: string}, message?: string}>}
     */
    register: async ({ firstName, lastName, email, password }) => {
        try {
            const response = await apiClient.post('/api/website-user', {
                firstName,
                lastName,
                email,
                password,
            });
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Registration failed',
            };
        }
    },

    /**
     * Request password reset
     * @param {string} email - User email
     * @returns {Promise<{success: boolean, message?: string}>}
     */
    forgotPassword: async (email) => {
        try {
            const response = await apiClient.post('/api/website-user/forgot-password', {
                email,
            });
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to send reset email',
            };
        }
    },

    /**
     * Reset password with token
     * @param {string} token - Reset token
     * @param {string} newPassword - New password
     * @returns {Promise<{success: boolean, message?: string}>}
     */
    resetPassword: async (token, newPassword) => {
        try {
            const response = await apiClient.post('/api/website-user/reset-password', {
                token,
                newPassword,
            });
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to reset password',
            };
        }
    },
};

export default authApi;
